import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Droplets, Mountain, Thermometer, Hammer, Phone, MapPin, ShieldCheck, Star, ChevronRight } from "lucide-react";
import { SITE, SERVICES, STATS } from "@/lib/site";
import { api } from "@/lib/api";
import ReferencesShowcase from "@/components/ReferencesShowcase";

const ICONS = { Droplets, Mountain, Thermometer, Hammer };

const Hero = () => (
  <section className="relative min-h-[92vh] flex items-end grain overflow-hidden" data-testid="hero-section">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${SITE.hero})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/85 via-[#0a0a0a]/55 to-[#0a0a0a]" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/85 via-transparent to-transparent" />
    <div className="relative w-full px-6 md:px-12 lg:px-16 pb-20 pt-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-8">
        <div className="inline-flex items-center gap-3 mb-6 font-mono text-[11px] tracking-[0.32em] uppercase text-[#d4af37]">
          <span className="h-px w-12 bg-[#d4af37]" />
          Eskişehir · TR-26 · EST. {SITE.founded}
        </div>
        <h1 className="font-display font-extrabold tracking-tighter leading-[0.85] text-white text-5xl sm:text-7xl lg:text-[9.5rem] text-balance">
          Yalıtımın
          <br />
          <span className="gold-shimmer">Lüks Standardı.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-white/65 text-lg leading-relaxed text-pretty">
          Su, çatı ve ısı yalıtımında 20+ yıllık ustalık. Premium malzeme, kusursuz işçilik ve
          <span className="text-[#d4af37]"> 10 yıla varan garanti</span> ile Eskişehir&apos;in en seçkin projelerinde imzamız var.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/iletisim"
            data-testid="hero-cta-keshif"
            className="group inline-flex items-center gap-3 bg-[#d4af37] text-[#121212] px-7 py-4 font-display uppercase tracking-[0.18em] text-sm hover:bg-white transition"
          >
            Ücretsiz Keşif Talep Et
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
          </Link>
          <a
            href={`tel:${SITE.phones[0].value}`}
            data-testid="hero-cta-call"
            className="inline-flex items-center gap-3 border border-white/25 hover:border-[#d4af37] hover:text-[#d4af37] text-white px-7 py-4 font-display uppercase tracking-[0.18em] text-sm transition"
          >
            <Phone className="h-4 w-4" /> {SITE.phones[0].label}
          </a>
        </div>
      </div>
      <aside className="lg:col-span-4 self-end">
        <div className="glass p-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">Hizmet Bölgeleri</p>
          <div className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-white/75">
            <span>· Tepebaşı</span>
            <span>· Odunpazarı</span>
            <span>· Çifteler</span>
            <span>· Sivrihisar</span>
            <span>· Mahmudiye</span>
            <span>· Mihalıççık</span>
          </div>
          <div className="gold-divider my-5" />
          <div className="flex items-center gap-3 text-white/70 text-sm">
            <ShieldCheck className="h-5 w-5 text-[#d4af37]" />
            <span>10 yıl işçilik garantisi</span>
          </div>
        </div>
      </aside>
    </div>
  </section>
);

const StatsRow = () => (
  <section className="border-y border-white/5 bg-[#0a0a0a]" data-testid="stats-section">
    <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
      {STATS.map((s) => (
        <div key={s.label} className="p-8 md:p-10">
          <div className="font-display text-4xl md:text-6xl text-[#d4af37] tracking-tighter">{s.value}</div>
          <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/55">{s.label}</div>
        </div>
      ))}
    </div>
  </section>
);

const ServicesGrid = () => (
  <section className="px-6 md:px-12 py-24 md:py-32" data-testid="services-section">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
      <div className="lg:col-span-7">
        <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#d4af37]">— Hizmetlerimiz</p>
        <h2 className="mt-4 font-display font-extrabold tracking-tighter text-5xl md:text-7xl leading-[0.9]">
          Dört temel disiplin,
          <br />
          <span className="text-[#d4af37]">tek bir</span> ustalık.
        </h2>
      </div>
      <p className="lg:col-span-5 text-white/55 text-base md:text-lg leading-relaxed">
        Her projeye keşif ile başlar, malzeme ve uygulamayı bina özelinde tasarlar, işçiliğimizi garanti ile imzalarız.
      </p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 auto-rows-[280px]">
      {SERVICES.map((s, i) => {
        const Ic = ICONS[s.icon];
        const wide = i === 0 || i === 3;
        return (
          <Link
            key={s.slug}
            to={`/hizmetlerimiz/${s.slug}`}
            data-testid={`service-card-${s.slug}`}
            className={`group relative overflow-hidden bg-[#0a0a0a] ring-1 ring-white/5 hover:ring-[#d4af37]/60 transition ${
              wide ? "lg:col-span-7" : "lg:col-span-5"
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center scale-105 group-hover:scale-110 transition duration-700"
              style={{ backgroundImage: `url(${s.image})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
            <div className="relative h-full p-7 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <Ic className="h-9 w-9 text-[#d4af37]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">0{i + 1}</span>
              </div>
              <div>
                <h3 className="font-display text-3xl md:text-4xl tracking-tight text-white">{s.title}</h3>
                <p className="mt-2 text-white/65 text-sm max-w-md">{s.short}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-[#d4af37] font-display text-xs uppercase tracking-[0.25em]">
                  Detay <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                </div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  </section>
);

const ProjectsTeaser = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/buildings").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);
  return (
    <section className="px-6 md:px-12 py-24 bg-[#0a0a0a] border-y border-white/5" data-testid="projects-teaser">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#d4af37]">— Projelerimiz</p>
          <h2 className="mt-3 font-display font-extrabold tracking-tighter text-5xl md:text-6xl">Devam eden imzalarımız</h2>
        </div>
        <Link to="/projelerimiz" data-testid="projects-see-all" className="inline-flex items-center gap-2 font-display uppercase tracking-[0.2em] text-sm text-[#d4af37] hover:text-white transition">
          Tüm Projeler <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-10 text-center text-white/45 font-mono text-xs uppercase tracking-[0.3em]" data-testid="projects-empty">
          Yakında ilk projelerimizi burada paylaşacağız.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.slice(0, 6).map((b, i) => (
            <Link key={b.id} to={`/projelerimiz`} className="group relative aspect-[4/5] overflow-hidden bg-[#121212] ring-1 ring-white/5 hover:ring-[#d4af37]/60 transition">
              <div className="absolute inset-0 bg-cover bg-center scale-105 group-hover:scale-110 transition duration-700" style={{ backgroundImage: `url(${b.image_url || SERVICES[i % 4].image})` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4af37]">{b.status}</div>
                <div className="font-display text-2xl md:text-3xl mt-1 text-white">{b.name}</div>
                {b.address && <div className="text-white/55 text-sm mt-1 truncate">{b.address}</div>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

const ProcessSection = () => (
  <section className="px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="process-section">
    <div className="lg:col-span-5">
      <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#d4af37]">— Süreç</p>
      <h2 className="mt-3 font-display font-extrabold tracking-tighter text-5xl md:text-6xl leading-[0.9]">
        4 adımda
        <br /> kusursuz yalıtım.
      </h2>
      <p className="mt-6 text-white/55 max-w-md">
        Keşiften kalıcı teslimata, her aşamada şeffaf raporlama ve sahada uzman ekip.
      </p>
    </div>
    <ol className="lg:col-span-7 space-y-3">
      {[
        ["Ücretsiz Keşif", "Uzman ekibimiz sahaya çıkar, ölçü ve fotoğraflarla detaylı analiz yapar."],
        ["Lüks Teklif & Sözleşme", "Şeffaf maliyet, malzeme detayları ve garanti şartlarını içeren özel teklif."],
        ["Uygulama", "Sertifikalı ustalar, premium malzeme ve günlük ilerleme raporu."],
        ["Teslim & Garanti", "Performans testi, fotoğraflı teslim ve 10 yıla varan işçilik garantisi."],
      ].map(([t, d], i) => (
        <li key={t} className="group flex gap-6 border-b border-white/5 py-6 hover:border-[#d4af37]/40 transition">
          <div className="font-mono text-[#d4af37] text-sm w-10 shrink-0">0{i + 1}</div>
          <div>
            <div className="font-display text-2xl md:text-3xl tracking-tight">{t}</div>
            <p className="text-white/55 mt-2 text-sm md:text-base max-w-2xl">{d}</p>
          </div>
        </li>
      ))}
    </ol>
  </section>
);

const CTABand = () => (
  <section className="relative px-6 md:px-12 py-20 md:py-28 bg-[#0a0a0a] border-y border-[#d4af37]/15 grain" data-testid="cta-band">
    <div className="max-w-5xl">
      <h2 className="font-display font-extrabold tracking-tighter text-5xl md:text-7xl leading-[0.9] text-balance">
        Projenizi konuşalım.
        <br /> <span className="text-[#d4af37]">Keşif ücretsiz.</span>
      </h2>
      <div className="mt-10 flex flex-wrap items-center gap-4">
        {SITE.phones.map((p) => (
          <a
            key={p.value}
            href={`tel:${p.value}`}
            data-testid={`cta-phone-${p.value}`}
            className="inline-flex items-center gap-3 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#121212] px-6 py-4 font-display text-sm tracking-[0.2em] uppercase transition"
          >
            <Phone className="h-4 w-4" /> {p.label}
          </a>
        ))}
        <Link
          to="/iletisim"
          data-testid="cta-contact"
          className="inline-flex items-center gap-3 bg-[#d4af37] text-[#121212] hover:bg-white px-6 py-4 font-display text-sm tracking-[0.2em] uppercase transition"
        >
          Online Form <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <StatsRow />
      <ServicesGrid />
      <ReferencesShowcase />
      <ProjectsTeaser />
      <ProcessSection />
      <CTABand />
    </div>
  );
}
